import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/Button";
import { siteConfig, navLinks } from "@/data/siteConfig";

export function AnnouncementBar() {
  return (
    <div className="bg-ink text-white text-center relative overflow-hidden" data-testid="announcement-bar">
      <div className="tech-grid-dark absolute inset-0" />
      <div className="relative mx-auto flex max-w-7xl flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 px-4 py-2.5 text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase">
        <span className="text-white/85">{siteConfig.company.tagline}</span>
        <Link to="/request-a-quote" className="hidden lg:inline-flex text-amber hover:text-amber-hover transition-colors duration-200 underline-offset-4 hover:underline" data-testid="announcement-quote-cta">
          Request a Quote →
        </Link>
      </div>
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <AnnouncementBar />
      <header className={`sticky top-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 border-b ${scrolled ? "bg-white/95 backdrop-blur border-[#E3E8EF] shadow-[0_2px_20px_rgba(14,34,56,0.06)]" : "bg-white border-transparent"}`} data-testid="site-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 py-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-body text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-amber-hover ${pathname === l.to ? "text-amber-hover" : "text-ink"}`}>
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:block">
            <Button to="/request-a-quote" variant="accent" arrow data-testid="header-quote-btn">Request a Quote</Button>
          </div>
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 text-ink focus-ring" aria-label="Toggle menu" aria-expanded={open} data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#E3E8EF] bg-white" data-testid="mobile-menu">
            <nav className="flex flex-col px-4 py-4" aria-label="Mobile">
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} className="py-3 font-display uppercase text-xl font-semibold text-ink border-b border-[#EEF1F5] hover:text-amber-hover transition-colors" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              <Button to="/request-a-quote" variant="accent" arrow className="mt-4 w-full" data-testid="mobile-quote-btn">Request a Quote</Button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
